import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { PublicPageTemplate, PublicFinalCTA } from '@/components/public';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getCanonicalUrl } from '@/config/site';
import { createBreadcrumbSchema, createCollectionPageSchema, createOrganizationSchema } from '@/config/seo';
import { AgentService } from '@/services/agent/core';
import { PlaybookService } from '@/services/playbooks/PlaybookService';
import { UserService } from '@/services/users/UserService';
import { cn } from '@/lib/utils';

import { PlaybookHero } from './components/PlaybookHero';
import { PlaybookConversationDemo } from './components/PlaybookConversationDemo';
import { PlaybookFlowSteps } from './components/PlaybookFlowSteps';
import { PlaybookCapabilities } from './components/PlaybookCapabilities';
import { PlaybookPlatformPreview } from './components/PlaybookPlatformPreview';
import { getPlaybookImage, categoryHeroImages } from './playbook-images';

const PlaybookPage = () => {
  const { categorySlug, playbookSlug } = useParams();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categorySlug, playbookSlug]);

  // Get playbook data
  const playbook = PlaybookService.getPlaybook(playbookSlug || '');
  const category = PlaybookService.getCategory(categorySlug || '');
  const relatedPlaybooks = playbook ? PlaybookService.getRelatedPlaybooks(playbook.meta.id, 3) : [];

  if (!playbook || !category) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-2">Playbook not found</h2>
          <Button onClick={() => navigate('/playbooks')}>Back to Playbooks</Button>
        </div>
      </div>
    );
  }

  const handleDeploy = async () => {
    const {
      data: { user },
    } = await UserService.getUser();

    if (!user) {
      navigate('/signin');
      return;
    }

    setIsCreating(true);

    try {
      const agentParams = PlaybookService.generateAgentConfigFromPlaybook(playbook);
      const supportEmails = user.email ? [user.email] : [];
      const fullParams = {
        ...agentParams,
        supportEmails,
        attachPhoneNumber: false,
        sendConversationReports: true,
      };

      const createdAgent = await AgentService.create(fullParams);

      if (createdAgent) {
        toast.success(`"${playbook.meta.name}" genie created successfully!`);
        navigate('/genie-hub/my-genies');
      }
    } catch (error: any) {
      console.error('Failed to create agent:', error);
      const message = error?.message || 'Failed to create genie. Please try again.';
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleTryDemo = () => {
    navigate('/live-agent/business-onboarding');
  };

  const seoConfig = playbook.seo
    ? {
        title: playbook.seo.metaTitle,
        description: playbook.seo.metaDescription,
        keywords: playbook.seo.keywords,
        ogTitle: playbook.seo.metaTitle,
        ogDescription: playbook.seo.metaDescription,
        ogImage: playbook.seo.ogImage || '/og-playbook-default.png',
        canonicalUrl: getCanonicalUrl(PlaybookService.getPlaybookUrl(playbook)),
        structuredData: [
          createBreadcrumbSchema([
            { name: 'Home', url: getCanonicalUrl('/') },
            { name: 'Playbooks', url: getCanonicalUrl('/playbooks') },
            { name: category.name, url: getCanonicalUrl(`/playbooks/${category.slug}`) },
            { name: playbook.meta.name, url: getCanonicalUrl(PlaybookService.getPlaybookUrl(playbook)) },
          ]),
          createCollectionPageSchema(playbook.meta.name, playbook.meta.description, getCanonicalUrl(PlaybookService.getPlaybookUrl(playbook))),
          createOrganizationSchema(),
        ],
      }
    : {
        title: `${playbook.meta.name} | Help Genie`,
        description: playbook.meta.description,
        structuredData: [
          createBreadcrumbSchema([
            { name: 'Home', url: getCanonicalUrl('/') },
            { name: 'Playbooks', url: getCanonicalUrl('/playbooks') },
            { name: category.name, url: getCanonicalUrl(`/playbooks/${category.slug}`) },
            { name: playbook.meta.name, url: getCanonicalUrl(PlaybookService.getPlaybookUrl(playbook)) },
          ]),
          createCollectionPageSchema(playbook.meta.name, playbook.meta.description, getCanonicalUrl(PlaybookService.getPlaybookUrl(playbook))),
          createOrganizationSchema(),
        ],
      };

  const strategyImage = getPlaybookImage(playbook.meta.id, category.slug);

  return (
    <PublicPageTemplate seo={seoConfig}>
      {/* Hero */}
      <PlaybookHero
        playbook={playbook}
        category={category}
        onDeploy={handleDeploy}
        onTryDemo={handleTryDemo}
        isCreating={isCreating}
      />

      {/* Strategy + Benefits Section */}
      <StrategySection
        playbook={playbook}
        strategyImage={strategyImage}
      />

      {/* Conversation Demo */}
      {playbook.demoScript && playbook.demoScript.length > 0 && (
        <PlaybookConversationDemo
          demoScript={playbook.demoScript}
          benefits={playbook.meta.benefits}
          playbookName={playbook.meta.name}
        />
      )}

      {/* Flow Steps */}
      {playbook.flowSteps && playbook.flowSteps.length > 0 && (
        <PlaybookFlowSteps steps={playbook.flowSteps} />
      )}

      {/* What You Get */}
      <PlaybookCapabilities />

      {/* Platform Preview */}
      <PlaybookPlatformPreview />

      {/* Related Playbooks */}
      {relatedPlaybooks.length > 0 && (
        <RelatedPlaybooksSection
          playbooks={relatedPlaybooks}
          onNavigate={(url) => navigate(url)}
        />
      )}

      {/* Final CTA */}
      <PublicFinalCTA
        backgroundImage={strategyImage}
        gradientOverlay="bg-gradient-to-br from-[#28406F] to-[#4E9CFF]"
        title={`Ready to deploy ${playbook.meta.name}?`}
        description="Set up your voice genie and start converting conversations into results."
        primaryButton={{
          text: isCreating ? 'Deploying...' : 'Start Free Trial',
          onClick: handleDeploy,
          disabled: isCreating,
        }}
        secondaryButton={{
          text: 'Try Voice Demo First',
          onClick: handleTryDemo,
        }}
        disclaimer="No credit card required • Cancel anytime"
      />
    </PublicPageTemplate>
  );
};

// --- Sub-sections ---

const StrategySection = ({
  playbook,
  strategyImage,
}: {
  playbook: ReturnType<typeof PlaybookService.getPlaybook>;
  strategyImage: string;
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  if (!playbook) return null;

  return (
    <section ref={sectionRef} className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div
            className={cn(
              'order-2 lg:order-1 transition-all duration-700 ease-out',
              isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <div className="relative overflow-hidden rounded-2xl shadow-2xl group" style={{ height: '500px' }}>
              <img
                src={strategyImage}
                alt="Strategy visualization"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                onError={(e) => {
                  e.currentTarget.src = '/hero-background.webp';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#28406F]/60 via-transparent to-[#4E9CFF]/30" />
            </div>
          </div>

          {/* Content */}
          <div
            className={cn(
              'order-1 lg:order-2 transition-all duration-700 ease-out delay-150',
              isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <div className="h-1 w-16 bg-gradient-to-r from-primary to-secondary rounded-full mb-6" />
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">The Strategy</h2>
            <p className="text-xl text-gray-600 mb-8">{playbook.meta.strategy}</p>

            {/* Benefits */}
            {playbook.meta.benefits && (
              <div className="space-y-3">
                {playbook.meta.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="p-1.5 bg-primary/10 rounded-lg flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Target Audience */}
            {playbook.meta.targetAudience && playbook.meta.targetAudience.length > 0 && (
              <div className="mt-8">
                <p className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wider">Perfect for</p>
                <div className="flex flex-wrap gap-2">
                  {playbook.meta.targetAudience.map((audience, idx) => (
                    <Badge key={idx} variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                      {audience}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const RelatedPlaybooksSection = ({
  playbooks,
  onNavigate,
}: {
  playbooks: NonNullable<ReturnType<typeof PlaybookService.getRelatedPlaybooks>>;
  onNavigate: (url: string) => void;
}) => {
  return (
    <section className="py-24 md:py-32 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Explore More Playbooks
          </h2>
          <p className="text-lg text-gray-500 mb-8">
            Discover strategies that complement your growth
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {playbooks.map((related) => {
            const relatedCategory = PlaybookService.getCategory(related.meta.categoryId);
            const heroImage = getPlaybookImage(related.meta.id, relatedCategory?.slug);

            return (
              <div
                key={related.meta.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
                onClick={() => onNavigate(PlaybookService.getPlaybookUrl(related))}
              >
                {/* Card image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={heroImage}
                    alt={related.meta.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.currentTarget.src = '/hero-background.webp';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                      {relatedCategory?.name || 'Playbook'}
                    </Badge>
                  </div>
                </div>

                {/* Card content */}
                <div className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    {related.meta.name}
                  </h4>
                  <p className="text-sm text-primary font-medium mb-3">{related.meta.tagline}</p>
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 mb-4">
                    {related.meta.strategy}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-end pt-4 border-t border-gray-100">
                    <ChevronRight className="h-5 w-5 text-primary group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <Button
            onClick={() => onNavigate('/playbooks')}
            size="lg"
            className="bg-gradient-to-r from-[#4E9CFF] to-[#28406F] hover:from-[#3d8de6] hover:to-[#1f3459] text-white font-semibold shadow-lg"
          >
            View All Playbooks
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PlaybookPage;
